/* global WHITELABEL */
/* eslint import/order: "off" */
/* eslint new-cap: "off" */

import { setParametros } from './init-globals' // PARAMETROS
import apiUserConfig from '@api/apiUserConfig'
import Vue from 'vue'
import App from './app'
import store from '@state/store'
import vuetify from './plugins/vuetify'
import './plugins/vue-apexcharts'
import './plugins/axios'
// ObservabilityPlugin is loaded lazily after bootstrap (see end of file) so it
// never sits on the critical path. The mixin `$gtagEvent` below talks to it
// through the global `window.__aawzObservabilityTrack` hook the plugin exposes
// once it has installed.
import './plugins/chartjs'
// import './plugins/gtag'
// import 'vuetify/src/styles/styles.sass'
// import 'vuetify/dist/vuetify.min.css'
import '@components/_globals'
import './design/main.sass'

import '@services/notification'
import '@utils/moment_locale'
import VCurrencyField from 'v-currency-field'
import InputByType from '@components/input-by-type.vue'
import { VTextField } from 'vuetify/lib' // Globally import VTextField
import './registerServiceWorker'

import VueChatScroll from 'vue-chat-scroll'
import Teleport from '@components/teleport/teleport.vue'
import TeleportTarget from '@components/teleport/teleport-target.vue'

import wsWorkspace from '@services/wsWorkspace'
import con from '@services/connection'
import * as STYLES from '@constants/Styles'
import { FEATURES, TELEMETRIA } from '@constants/Text'

// Arquivo para init
import '@utils/InitFunctions'
// import * as Sentry from '@sentry/browser'
import * as Sentry from '@sentry/vue'
import { Vue as VueIntegration } from '@sentry/integrations'
// import { BrowserTracing } from '@sentry/tracing'

import { registerAllModules } from '@src/modules'
import {
  textOnColor,
  textOnColorRgb,
  brightnessColor,
} from '@modules/global/utils/Color'

import IconSVG from '@components/icons/IconSVG.vue'

import { GlobalBus } from '@services/bus'
import {
  isNonErrorPrimitiveRejection,
  setupGlobalRejectionHandler,
} from '@utils/sentryFilters'
import {
  extractErrorParts,
  extractRequestInfo,
  MESSAGE_MAX_LENGTH,
  STACK_MAX_LENGTH,
} from '@utils/stringifyError'
import {
  clearChunkReloadFlag,
  clearReloadAttempts,
} from '@utils/chunkErrorHandler'
import {
  installChunkErrorGlobalHandler,
  isSentryChunkLoadError,
} from '@utils/chunkErrorGlobalHandler'
// eslint-disable-next-line no-unused-vars
import {
  // eslint-disable-next-line no-unused-vars
  startHeartbeat as startVersionCheckHeartbeat,
  // eslint-disable-next-line no-unused-vars
  bindVisibilityListener as bindVersionCheckVisibility,
} from '@services/version-check'
// eslint-disable-next-line no-unused-vars
import { getAppVariant, APP_VARIANT_BETA } from '@utils/environment'

import '@services/parameters.js'
import { setupBootSplashSync, hideBootSplash } from '@services/boot-splash'
import router from '@router'

Vue.component('icon-svg', IconSVG)
Vue.component('InputByType', InputByType)

Vue.component('v-text-field', VTextField)

/**
 * Espelho de todo `$gtagEvent` no GlobalBus (`TELEMETRIA.EVENTO`). É assim
 * que a pesquisa de NPS sabe que uma ação de negócio foi CONCLUÍDA sem cada
 * handler precisar conhecê-la. Chamado ANTES do guard `isDev` e do
 * `if (!gtag)` de propósito: em dev/whitelabelh o sinal precisa chegar.
 */
function espelharEventoNoBus(evento) {
  GlobalBus.$emit(TELEMETRIA.EVENTO, evento)
}

/** Observabilidade (Loki) + bus interno, antes de qualquer guard do GA. */
function difundirEvento(evento) {
  forwardEventToObservability(evento)
  espelharEventoNoBus(evento)
}

// Forward GA4 events to the observability/Loki pipeline (fire-and-forget).
// No-op until the lazy ObservabilityPlugin installs and exposes the hook.
function forwardEventToObservability({
  module,
  action,
  value,
  eventName,
  additionalParams,
}) {
  try {
    const track =
      typeof globalThis !== 'undefined' &&
      globalThis.window &&
      globalThis.window.__aawzObservabilityTrack
    if (typeof track === 'function') {
      // When both are present and differ, eventName wins so callers that pass
      // an explicit eventName (e.g. 'simulador_simular' vs action='simular')
      // don't collapse distinct events under the same name in Loki.
      const resolvedEventName =
        eventName && eventName !== action ? eventName : action || eventName
      track({
        eventName: resolvedEventName,
        module,
        value,
        bannerId: additionalParams && additionalParams.banner_id,
        payload: additionalParams,
      })
    }
  } catch (_telemetryError) {
    // Telemetry failures must never break $gtagEvent — the original GA4
    // call below depends on this function returning normally.
  }
}

// Pre-install buffer for early observability events (errors + web vitals)
// fired BEFORE the lazy ObservabilityPlugin chunk finishes loading. Without
// this, LCP/FID/CLS sampled before ~1.5s of bootstrap and uncaught errors at
// boot are silently dropped. The plugin drains this buffer on install.
const OBSERVABILITY_EARLY_BUFFER = []
const OBSERVABILITY_EARLY_BUFFER_MAX = 50

function captureEarlyError(input, stack, source, context) {
  if (OBSERVABILITY_EARLY_BUFFER.length >= OBSERVABILITY_EARLY_BUFFER_MAX)
    return
  // Accept either a raw error/reason or a pre-extracted string.
  // extractErrorParts is the single source of truth — prevents both
  // "[object Object]" and empty strings from leaking into client_message
  // before the lazy plugin has installed.
  const parts = extractErrorParts(input, stack)
  const request = extractRequestInfo(input)
  const ctx = context || {}
  OBSERVABILITY_EARLY_BUFFER.push({
    type: 'error',
    message: parts.message.slice(0, MESSAGE_MAX_LENGTH),
    stack: parts.stack.slice(0, STACK_MAX_LENGTH),
    source: source || 'early',
    context: {
      errorName: parts.name,
      errorType: ctx.errorType || '',
      httpStatus: request.httpStatus,
      requestUrl: request.requestUrl,
      sourceFile: ctx.sourceFile || '',
      sourceLine: ctx.sourceLine != null ? ctx.sourceLine : null,
      sourceCol: ctx.sourceCol != null ? ctx.sourceCol : null,
    },
    ts: Date.now(),
  })
}

function setupEarlyObservabilityBuffer() {
  if (typeof globalThis === 'undefined' || !globalThis.window) return
  globalThis.window.addEventListener('error', (event) => {
    captureEarlyError(
      event.error || event.message,
      event.error && event.error.stack,
      'window.error',
      {
        errorType: 'runtime',
        sourceFile: event.filename || '',
        sourceLine: typeof event.lineno === 'number' ? event.lineno : null,
        sourceCol: typeof event.colno === 'number' ? event.colno : null,
      }
    )
  })
  globalThis.window.addEventListener('unhandledrejection', (event) => {
    captureEarlyError(event.reason, undefined, 'unhandledrejection', {
      errorType: 'unhandledrejection',
    })
  })
}

function drainEarlyObservabilityBuffer() {
  if (typeof globalThis === 'undefined' || !globalThis.window) return
  // Use the captureError hook so message/stack/source bypass the PII allowlist
  // applied to custom_event payloads (those fields are mandatory error context,
  // not user payload). Falls back to the custom_event hook only if missing.
  const capture = globalThis.window.__aawzObservabilityCaptureError
  const track = globalThis.window.__aawzObservabilityTrack
  if (typeof capture !== 'function' && typeof track !== 'function') return
  for (const item of OBSERVABILITY_EARLY_BUFFER) {
    if (typeof capture === 'function') {
      capture(
        item.message,
        item.stack,
        item.source || 'bootstrap',
        item.context
      )
      continue
    }
    track({
      eventName: 'early_' + item.type,
      module: 'bootstrap',
      value: 0,
    })
  }
  OBSERVABILITY_EARLY_BUFFER.length = 0
}

// Wires up Sentry (when in observability-enabled envs), the global
// unhandled-rejection handler, and the lazy ObservabilityPlugin loader.
function setupTelemetryAndErrors(router) {
  installChunkErrorGlobalHandler()
  setupGlobalRejectionHandler()
  setupEarlyObservabilityBuffer()
  const isProduction = process.env.VUE_APP_OBSERVABILITY_ENABLED === 'true'
  if (isProduction) initSentry()
  scheduleObservabilityLoad(router)
}

// Lazy-load the ObservabilityPlugin in its own webpack chunk so it never
// blocks LCP/TTI. The mixin `$gtagEvent` already talks to it through the
// `window.__aawzObservabilityTrack` hook (no-op until plugin installs).
function scheduleObservabilityLoad(router) {
  const start = () => {
    import(/* webpackChunkName: "observability" */ './plugins/observability')
      .then(({ default: ObservabilityPlugin }) => {
        Vue.use(ObservabilityPlugin, { router })
        // Drain anything captured between bootstrap start and plugin install
        // so early LCP/FID/error events are not silently lost.
        drainEarlyObservabilityBuffer()
      })
      .catch((err) => {
        console.warn('Failed to load observability plugin', err)
      })
  }

  if (
    typeof globalThis !== 'undefined' &&
    globalThis.window &&
    typeof globalThis.window.requestIdleCallback === 'function'
  ) {
    globalThis.window.requestIdleCallback(start, { timeout: 3000 })
  } else {
    setTimeout(start, 1500)
  }
}

// Don't warn about using the dev version of Vue in development.
Vue.config.productionTip = process.env.NODE_ENV === 'production'

// If running inside Cypress...
if (process.env.VUE_APP_TEST === 'e2e') {
  // Ensure tests fail when Vue emits an error.
  Vue.config.errorHandler = window.Cypress.cy.onUncaughtException
}
Vue.use(VueChatScroll)
// Teleport caseiro injeta o
// header da view (BasePageHeader) no topo do shell (NavBarNavigation), acima das
// abas. Ver src/components/teleport/.
Vue.component('Teleport', Teleport)
Vue.component('TeleportTarget', TeleportTarget)
Vue.prototype.$wsWorkspace = new wsWorkspace()

Vue.mixin({
  computed: {
    $GlobalBus() {
      return GlobalBus
    },
    $access() {
      return store.getters['parameters/show'] || {}
    },
    $hideCrossSell() {
      return Boolean(this.$access[FEATURES.OCULTAR_CROSS_SELL])
    },
    $canSendLinks() {
      return (
        store.getters['parameters/canShow']('services') &&
        store.getters['parameters/canShow']('communications-client')
      )
    },
    $con() {
      return con
    },
    $STYLES() {
      return { ...STYLES }
    },
    $showWorkspace() {
      return window.SHOW_WORKSPACE
    },
    $palette() {
      return store.getters['parameters/paleta'] || []
    },
    $isSmallScreen() {
      return this.$vuetify.breakpoint.smAndDown
    },
    $isMediumScreen() {
      return this.$vuetify.breakpoint.mdAndDown
    },
  },
  methods: {
    $gtagEvent({
      module = 'general',
      action,
      value,
      eventName = 'click',
      additionalParams = {},
    }) {
      difundirEvento({ module, action, value, eventName, additionalParams })

      const gtag = this.$gtag || Vue.$gtag
      if (!gtag) {
        console.log('GTAG não encontrado', this.$gtag, Vue.$gtag)
        return
      }
      const isDev = [
        'dev.aawz.com.br',
        'whitelabelh.aawz.com.br',
        'whitelabel.aawz.local.com.br',
        'localhost',
      ].includes(window.location.hostname)
      if (isDev) {
        return
      }
      try {
        // Format event parameters using only accepted GA4 parameters
        let eventParams = {
          // Standard GA4 parameters
          event_category: module,
          event_label: action,
          value: value,

          // Include any other valid GA4 parameters
          non_interaction: additionalParams.non_interaction || false,

          // Include custom dimension parameters (these are tracked in GA4 configuration)
          custom_dimensions: additionalParams,
        }
        if (additionalParams && additionalParams.banner_id) {
          eventParams = {
            ...eventParams,
            // Add custom parameters that fit the GA4 structure
            content_id: additionalParams.banner_id,
            content_type: 'banner',
            description: additionalParams.banner_name,
          }
        }

        gtag.event(eventName, eventParams)
      } catch (err) {
        console.log(err)
      }
    },
    $setOpacity(hex, alpha) {
      return `${hex}${Math.floor(alpha * 255)
        .toString(16)
        .padStart(2, 0)}`
    },
    $currentUser() {
      return store.getters['user/currentUser'] || {}
    },
    $currentClient() {
      return store.getters['user/currentClient'] || {}
    },
    $Color() {
      return store.getters['parameters/colors']
    },
    $configLocal() {
      return store.getters['parameters/config_local'] || {}
    },
    $showBigSnack(snack) {
      store.dispatch('global/addQueueBigSnack', snack)
    },
    $showSnack(snack) {
      store.dispatch('feedback/showSnack', snack)
    },
    $showSnackModal(snack) {
      store.dispatch('feedback/addQueueSnackModal', snack)
    },
    $hideSnack(snack) {
      store.dispatch('feedback/closeSnack', snack)
    },
    $showModal(modal) {
      store.dispatch('feedback/showModal', modal)
    },
    $hideModal(modal) {
      store.dispatch('feedback/closeModal', modal)
    },
    $isWhitelabel() {
      return WHITELABEL
    },
    $canAccess(func) {
      return store.getters['parameters/canShow'](func)
    },
    $canAccessRoute(routeName) {
      const route = router.matcher.getRoutes().find((item) => {
        return item.name === routeName
      })
      if (route) {
        if (
          route.meta &&
          route.meta.can_show &&
          !store.getters['parameters/canShow'](route.meta.can_show)
        ) {
          return false
        }
      }
      return true
    },
  },
})

function getVersion() {
  try {
    const versionFile = require('../versao.json')
    if (versionFile) {
      console.log('Arquivo de versao encontrado')
      window.__VERSION__ = versionFile.versao || ''
      return versionFile.versao || ''
    }
  } catch (err) {
    console.error(err)
    return '0.0.0'
  }
}

// Contraste sobre a navbar white-label (mesma lógica da nav antiga:
// textOnColor decide texto escuro/claro pela luminância do fundo). Viram
// --text-on-navbar / --text-on-navbar-rgb, usadas pelo shell navegacao-v2.
function deriveNavbarContrastColors(colors) {
  colors['text-on-navbar'] = textOnColor(colors['bg-navbar'])
  colors['text-on-navbar-rgb'] = textOnColorRgb(colors['bg-navbar'])
}

function updateColors(PARAMETROS) {
  console.log('updateColors', PARAMETROS)
  const textOnPrimary = textOnColor(PARAMETROS.colors.primary)
  PARAMETROS.colors['text-on-primary'] = textOnPrimary
  const textOnSecondary = textOnColor(PARAMETROS.colors.second)
  PARAMETROS.colors['text-on-second'] = textOnSecondary
  PARAMETROS.colors['primary-lighter'] = brightnessColor(
    PARAMETROS.colors.primary,
    80
  )
  PARAMETROS.colors['bg-navbar-header'] =
    PARAMETROS.officeColors['bg_navbar_header'] || '#FFF'
  PARAMETROS.colors['bg-navbar'] =
    PARAMETROS.officeColors['bg_navbar'] || '#030309'
  deriveNavbarContrastColors(PARAMETROS.colors)
  PARAMETROS.colors['text-navbar-active'] =
    PARAMETROS.officeColors['text_navbar_active'] || PARAMETROS.colors.primary
  store.dispatch('parameters/setEscritorioBase', PARAMETROS.escritorio)
  store.dispatch('parameters/setColors', { colors: PARAMETROS.colors, vuetify })
  store.dispatch('parameters/updateColors', {
    mode: 'light_mode',
    colors: PARAMETROS.colors,
  })

  setParametros(PARAMETROS)
}

function initSentry() {
  console.log('Iniciando sentry')
  // Prefixo do release derivado do BASE_URL inlined (mesma fonte do
  // app_variant no GA/Loki) — permite rodar build estável e beta deste
  // mesmo repo sem etiquetar erros do estável como release beta.
  const releasePrefix =
    getAppVariant() === APP_VARIANT_BETA ? 'vue-frontend-beta' : 'vue-frontend'
  Sentry.init({
    Vue: Vue,
    dsn: 'https://2ced4c26bceb4d80983d4cef8691bfe0@o437379.ingest.sentry.io/5404931',
    integrations: [
      new VueIntegration({ Vue, attachProps: true }),
      new Sentry.BrowserTracing(),
    ],
    // trackComponents cria um span por render de componente e tracesSampleRate 1
    // mantinha buffer de tracing permanente na RAM de todos os usuários (CU-86ajk2n86)
    tracesSampleRate: 0.1,
    autoSessionTracking: true,
    release: releasePrefix + '@' + getVersion(),
    environment: process.env.NODE_ENV || '',
    debug: process.env.NODE_ENV !== 'production',
    beforeSend(event) {
      if (isNonErrorPrimitiveRejection(event)) {
        return null
      }
      if (isSentryChunkLoadError(event)) {
        return null
      }
      return event
    },
  })
  Sentry.setTag('domain', window.location.host)
  Sentry.setTag('url', window.location.href)
}

function updateDesignParams(PARAMETROS) {
  store.dispatch('parameters/setLogo', PARAMETROS.logo)
  store.dispatch('parameters/setLogoBranca', PARAMETROS.logoBranca)
  store.dispatch('parameters/setFavicon', PARAMETROS.favicon)
  store.dispatch('parameters/setFaviconBranca', PARAMETROS.faviconBranca)
  store.dispatch('parameters/setBG', PARAMETROS.backgroundImage)
}

function checkOfficeColors(PARAMETROS) {
  if (typeof PARAMETROS.officeColors === 'string') {
    try {
      PARAMETROS.officeColors = JSON.parse(PARAMETROS.officeColors)
    } catch (err) {
      console.error(err)
    } finally {
      if (!PARAMETROS.officeColors) {
        PARAMETROS.officeColors = {}
      }
    }
  }
}

async function bootstrap() {
  const PARAMETROS = await apiUserConfig.loadConfig()

  checkOfficeColors(PARAMETROS)

  updateColors(PARAMETROS)

  updateDesignParams(PARAMETROS)

  window.SHOW_AUTOMATIC_RESCUE = true

  Vue.use(VCurrencyField, {
    locale: 'pt-BR',
    decimalLength: 2,
    autoDecimalMode: true,
    min: null,
    max: null,
    defaultValue: null,
  })

  await registerAllModules()

  // Boot da SPA: limpa a flag transitória de chunk-error setada pelo ciclo
  // anterior — esta é segura limpar antes do route load porque so afeta o
  // proximo handleChunkError (evita re-emitir modal logo apos o reload).
  clearChunkReloadFlag()

  // Camada proativa do versionamento (DESATIVADA até deploy do backend).
  //
  // O heartbeat e o visibility check dependem do endpoint
  // /server/spa-version/frontend e do <meta name="aawz-version"> injetado
  // pelo Flask (PR backend #890). Enquanto o backend não for deployado, a
  // camada PROATIVA fica off — só a camada REATIVA (router.onError →
  // ChunkLoadError → modal + forceReload) opera, que é independente do
  // backend.
  //
  // Para reativar: descomentar as chamadas abaixo + descomentar a
  // <meta name="aawz-version"> em public/index.html + reimportar
  // isLocalhost de @utils/environment.
  // if (!isLocalhost()) {
  //   startVersionCheckHeartbeat()
  //   bindVersionCheckVisibility()
  // }

  const app = new Vue({
    router,
    store,
    vuetify,
    render: (h) => h(App),
  }).$mount('#app')

  // O boot splash (overlay irmão de #app) cobre a "travada" do mount; depois que
  // o app pinta por baixo (CinematicLoader, idêntico), fazemos um fade-out suave
  // para um handoff fluido. Ver public/boot-splash.{css,js}.
  hideBootSplash()

  // Limpa o histórico de tentativas de reload SO depois que a navegação
  // inicial completar com sucesso (lazy chunk inicial carregado, sem
  // ChunkLoadError). Antes, limpávamos no boot — se o reload ainda servia
  // HTML/chunks antigos, o guard nunca acumulava entre tentativas e o
  // anti-loop não disparava na 3ª. Agora o histórico só zera quando a
  // navegação prova que o ambiente está saudável.
  router.onReady(
    () => clearReloadAttempts(),
    () => {
      /* swallow — chunk error já cuida via handleChunkError */
    }
  )

  setupTelemetryAndErrors(router)

  if (process.env.VUE_APP_TEST === 'e2e') {
    window.__app__ = app
  }
}

bootstrap()
setupBootSplashSync(store)
